/**
 * FILE: src/blueprint/components/report/TextBlock.jsx
 */

function asString(value) {
  return typeof value === 'string' ? value : '';
}

function containsHtml(text) {
  return /<\/?[a-z][\s\S]*>/i.test(String(text || ''));
}

function sanitizeHtml(html) {
  if (typeof document === 'undefined') {
    return String(html || '').trim();
  }

  const wrapper = document.createElement('div');
  wrapper.innerHTML = String(html || '');

  const allowedTags = new Set([
    'P',
    'BR',
    'STRONG',
    'EM',
    'B',
    'I',
    'U',
    'SPAN',
    'UL',
    'OL',
    'LI',
    'A',
  ]);

  const walk = (node) => {
    const children = Array.from(node.childNodes);

    children.forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child;
        const tagName = el.tagName.toUpperCase();

        if (!allowedTags.has(tagName)) {
          const fragment = document.createDocumentFragment();
          while (el.firstChild) {
            fragment.appendChild(el.firstChild);
          }
          el.replaceWith(fragment);
          walk(node);
          return;
        }

        Array.from(el.attributes).forEach((attr) => {
          const attrName = attr.name.toLowerCase();

          if (attrName === 'class') {
            const allowedClasses = new Set([
              'report-accent',
              'report-accent--list',
              'report-accent__item',
              'report-accent--html',
            ]);

            const safeClasses = attr.value
              .split(/\s+/)
              .map((className) => className.trim())
              .filter((className) => allowedClasses.has(className));

            if (safeClasses.length > 0) {
              el.setAttribute('class', safeClasses.join(' '));
              return;
            }
          }

          if (
            tagName === 'A' &&
            (attrName === 'href' || attrName === 'target' || attrName === 'rel')
          ) {
            return;
          }

          el.removeAttribute(attr.name);
        });

        if (tagName === 'A') {
          const href = asString(el.getAttribute('href')).trim();
          if (!href) {
            el.replaceWith(...el.childNodes);
            return;
          }

          el.setAttribute('rel', 'noopener noreferrer');
          if (!el.getAttribute('target')) {
            el.setAttribute('target', '_blank');
          }
        }

        walk(el);
        return;
      }

      if (child.nodeType === Node.COMMENT_NODE) {
        child.remove();
      }
    });
  };

  walk(wrapper);

  return wrapper.innerHTML.trim();
}

function PlainText({ value }) {
  const text = asString(value).trim();
  if (!text) return null;

  const paragraphs = text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split(/\n{2,}/g)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <>
      {paragraphs.map((paragraph, paragraphIndex) => {
        const lines = paragraph.split('\n');

        return (
          <p key={`p-${paragraphIndex}`} className="dv-block-renderer__paragraph">
            {lines.map((line, lineIndex) => (
              <span key={`p-${paragraphIndex}-l-${lineIndex}`}>
                {line}
                {lineIndex < lines.length - 1 ? <br /> : null}
              </span>
            ))}
          </p>
        );
      })}
    </>
  );
}

export default function TextBlock({ value, format = 'plain' }) {
  const text = asString(value).trim();
  if (!text) return null;

  const resolvedFormat = asString(format).trim() || 'plain';
  const shouldRenderHtml = resolvedFormat === 'html' || containsHtml(text);

  if (shouldRenderHtml) {
    const safeHtml = sanitizeHtml(text);
    if (!safeHtml) return null;

    return (
      <div className="dv-block-renderer__html" dangerouslySetInnerHTML={{ __html: safeHtml }} />
    );
  }

  return <PlainText value={text} />;
}
